/**
 * SignatureService
 * 
 * Service responsible for managing form signatures throughout the MacSafety web application.
 * Handles signature storage, validation, and retrieval using Angular signals, keeping
 * signature functionality separate from attachment management for flexibility and future extensibility.
 * 
 * Key Features:
 * - Signature storage and management using Angular signals
 * - Signature requirement validation and error reporting
 * - Computed signals for real-time signature validation status
 * - Support for multiple signatures per identifier
 * - Canvas to base64 conversion for signature data
 * - Integration with form submission workflow
 * - Backward compatibility with single signature methods
 * 
 * Note: This service is intentionally separate from AttachmentUploadService to maintain
 * flexibility for future signature-related changes. Signatures are currently handled
 * as PNG images but can be easily modified to support other formats or requirements.
 * 
 * Components that utilize this service:
 * - All form submission components requiring signatures
 * - Signature creation and capture components
 * - Form validation components checking signature requirements
 * - AttachmentUploadService (for preparing signatures as attachments)
 * - Any component needing signature management capabilities
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Injectable, signal, computed } from '@angular/core';
import { FormAttachmentUploadRequirements } from '../models/forms/attachments/form-attachment-requirements';
import { AttachmentUploadData, AttachmentDataForSubmission } from '../models/forms/attachments/attachment-upload';

@Injectable({
  providedIn: 'root'
})
export class SignatureService {
  // Private signal to store signatures organized by identifier
  // Supports multiple signatures per identifier for future extensibility
  private readonly _signatures = signal<Record<string, AttachmentDataForSubmission[]>>({});
  
  // Private signal for storing signature requirements from form configuration
  // Used for validation and determining if all required signatures are present
  private readonly _signatureRequirements = signal<FormAttachmentUploadRequirements | null>(null);

  // Public readonly signal for components to access signature data
  public readonly signatures = this._signatures.asReadonly();

  constructor() {}

  /**
   * Sets the signature requirements for the current form
   * Called during form initialization to establish signature validation rules
   * 
   * @param requirements - The form attachment requirements containing signature specifications
   */
  public setSignatureRequirements(requirements: FormAttachmentUploadRequirements) {
    this._signatureRequirements.set(requirements);
  }

  /**
   * Computed signal that checks if all required signatures are present and valid
   * Returns true if no requirements are set or if all required signatures exist
   * Automatically updates when signatures or requirements change
   * 
   * @returns True if all signature requirements are met, false otherwise
   */
  public readonly areSignaturesValid = computed(() => {
    const requirements = this._signatureRequirements();
    if (!requirements || requirements.signatures?.length === 0) return true;

    const currentSignatures = this._signatures();
    for (const requirement of requirements.signatures || []) {
      const signatures = currentSignatures[requirement.identifier] || [];
      if (requirement.required && signatures.length === 0) {
        console.log('signature is required but not present', requirement.identifier);
        return false;
      }
    }
    return true;
  });

  /**
   * Returns an array of validation error messages for missing required signatures
   * Useful for displaying user-friendly error messages in forms
   * 
   * @returns Array of error messages describing which signatures are missing
   */
  public getSignatureValidationErrors(): Array<string> {
    const errors: Array<string> = [];
    const requirements = this._signatureRequirements();
    if (!requirements) return errors;

    const currentSignatures = this._signatures();
    for (const requirement of requirements.signatures || []) {
      const signatures = currentSignatures[requirement.identifier] || [];
      if (signatures.length === 0) {
        errors.push(`Missing ${requirement.label}`);
      }
    }
    return errors;
  }

  /**
   * Retrieves all signatures for a specific identifier
   * Supports multiple signatures per identifier for future extensibility
   * 
   * @param identifier - The unique identifier of the signatures to retrieve
   * @returns Array of signature data if found, empty array otherwise
   */
  getSignatures(identifier: string): AttachmentDataForSubmission[] {
    return this._signatures()[identifier] || [];
  }

  /**
   * Retrieves the first signature for a specific identifier
   * Maintains backward compatibility with existing single-signature implementations
   * 
   * @param identifier - The unique identifier of the signature to retrieve
   * @returns The first signature data if found, undefined otherwise
   */
  getSignature(identifier: string): AttachmentDataForSubmission | undefined {
    const signatures = this.getSignatures(identifier);
    return signatures.length > 0 ? signatures[0] : undefined;
  }

  /**
   * Checks if signatures exist for a specific identifier
   * Useful for conditional UI rendering and validation logic
   * 
   * @param identifier - The unique identifier of the signatures to check
   * @returns True if signatures exist, false otherwise
   */
  hasSignature(identifier: string): boolean {
    const signatures = this.getSignatures(identifier);
    return signatures.length > 0;
  }

  /**
   * Updates or adds signatures for a specific identifier
   * Replaces any existing signatures for the identifier with the new array
   * 
   * @param identifier - The unique identifier for the signatures
   * @param signatures - Array of signature data to store
   */
  updateSignatures(identifier: string, signatures: AttachmentDataForSubmission[]) {
    this._signatures.update(current => ({
      ...current,
      [identifier]: signatures
    }));
  }

  /**
   * Updates or adds a single signature for a specific identifier
   * Maintains backward compatibility with existing single-signature implementations
   * Wraps the single signature in an array for consistency
   * 
   * @param identifier - The unique identifier for the signature
   * @param signature - The signature data to store
   */
  updateSignature(identifier: string, signature: AttachmentDataForSubmission) {
    this._signatures.update(current => ({
      ...current,
      [identifier]: [signature]
    }));
  }

  /**
   * Adds a signature to the existing array for a specific identifier
   * Appends to existing signatures rather than replacing them
   * Useful for collecting multiple signatures over time
   * 
   * @param identifier - The unique identifier for the signature
   * @param signature - The signature data to add
   */
  addSignature(identifier: string, signature: AttachmentDataForSubmission) {
    this._signatures.update(current => ({
      ...current,
      [identifier]: [...(current[identifier] || []), signature]
    }));
  }

  /**
   * Removes all signatures for a specific identifier
   * Completely removes the identifier and its associated signatures
   * 
   * @param identifier - The unique identifier of the signature to remove
   */
  removeSignature(identifier: string) {
    this._signatures.update(current => {
      const newSignatures = { ...current };
      delete newSignatures[identifier];
      return newSignatures;
    });
  }

  /**
   * Removes a specific signature by its identifier and index
   * Useful when managing multiple signatures per identifier
   * Automatically cleans up empty identifier entries
   * 
   * @param identifier - The unique identifier of the signature
   * @param index - The index of the signature to remove
   */
  removeSignatureAtIndex(identifier: string, index: number) {
    this._signatures.update(current => {
      const signatures = current[identifier] || [];
      const updatedSignatures = signatures.filter((_, i) => i !== index);
      
      if (updatedSignatures.length === 0) {
        const { [identifier]: _, ...rest } = current;
        return rest;
      }
      
      return {
        ...current,
        [identifier]: updatedSignatures
      };
    });
  }

  /**
   * Clears all stored signatures from the service
   * Useful for resetting state between different forms or sessions
   */
  clearSignatures() {
    this._signatures.set({});
  }

  /**
   * Converts a canvas element to a base64 encoded PNG image
   * Extracts the base64 data without the data URL prefix for efficient storage
   * Used by signature capture components to convert drawn signatures to storable format
   * 
   * @param canvas - The HTML canvas element containing the signature
   * @returns Promise resolving to the base64 encoded image string (without data URL prefix)
   */
  async canvasToBase64(canvas: HTMLCanvasElement): Promise<string> {
    const dataUrl = canvas.toDataURL('image/png');
    // Extract only the base64 part, removing the "data:image/png;base64," prefix
    return dataUrl.split(',')[1];
  }
} 